// =============================================================
// Cart quick-view modal
// -------------------------------------------------------------
// Intercepts clicks on product links inside merchant-authored
// cart content (PWYC message, rotating brand banner) and shows a
// product preview with variant picker + quantity + add-to-cart —
// an in-cart upsell path that never makes the customer leave.
//
// Product JSON fetched via Shopify's /products/{handle}.js
// endpoint. Add-to-cart posts to /cart/add.js and reloads the
// cart page on success so the new line item appears with correct
// pricing, discounts, and reward threshold progress.
//
// Trigger scope: any <a href="/products/..."> inside an element
// with the `data-cart-quick-view-scope` attribute. Stays off
// everywhere else so regular product links behave normally.
// =============================================================

(function () {
  if (window.__cartQuickViewInstalled) return;
  window.__cartQuickViewInstalled = true;

  const MODAL_ID = 'CartQuickView';
  let modal, panel, closeBtn, titleEl, priceEl, descEl, imageEl, mediaEl, statusEl;
  let formEl, variantPickerEl, qtyInputEl, addBtnEl, errorEl;
  let currentProduct = null;
  let previouslyFocused = null;

  // Module-scoped cache of fetched product JSON, keyed by handle. 5-minute
  // TTL so a user opening the same quick-view twice (e.g., Pay It Forward
  // tier comparison) doesn't re-fetch. Products rarely change mid-session;
  // caching eliminates 300-800ms on 3G webview traffic for repeat opens.
  const PRODUCT_CACHE_TTL_MS = 5 * 60 * 1000;
  const productCache = new Map();

  // Elements whose `inert` state we toggle when the modal opens/closes.
  // Everything except the modal gets inert to prevent tab-key escapes
  // and AT traversal into background content while the dialog is open.
  let inertedSiblings = [];

  function ensureModal() {
    if (modal) return modal;
    modal = document.getElementById(MODAL_ID);
    if (modal) return cacheRefs();

    modal = document.createElement('div');
    modal.id = MODAL_ID;
    modal.className = 'cart-quick-view';
    modal.hidden = true;
    modal.setAttribute('aria-hidden', 'true');
    modal.innerHTML = `
      <div class="cart-quick-view__backdrop" data-close></div>
      <div class="cart-quick-view__panel" role="dialog" aria-modal="true" aria-labelledby="${MODAL_ID}Title">
        <button class="cart-quick-view__close" type="button" data-close aria-label="Close quick view">&times;</button>
        <div class="cart-quick-view__status" data-status role="status" aria-live="polite">Loading&hellip;</div>
        <div class="cart-quick-view__body">
          <div class="cart-quick-view__media">
            <img class="cart-quick-view__image" alt="" loading="lazy">
          </div>
          <div class="cart-quick-view__info">
            <h2 class="cart-quick-view__title" id="${MODAL_ID}Title"></h2>
            <div class="cart-quick-view__price"></div>
            <div class="cart-quick-view__description"></div>
            <form class="cart-quick-view__form" novalidate>
              <div class="cart-quick-view__variants"></div>
              <div class="cart-quick-view__qty">
                <label class="cart-quick-view__qty-label" for="${MODAL_ID}Qty">Quantity</label>
                <div class="cart-quick-view__qty-stepper">
                  <button type="button" class="cart-quick-view__qty-btn" data-qty="-1" aria-label="Decrease quantity">&minus;</button>
                  <input id="${MODAL_ID}Qty" class="cart-quick-view__qty-input" type="number" name="quantity" value="1" min="1" max="99" inputmode="numeric">
                  <button type="button" class="cart-quick-view__qty-btn" data-qty="1" aria-label="Increase quantity">&plus;</button>
                </div>
              </div>
              <div class="cart-quick-view__error" data-error hidden></div>
              <div class="cart-quick-view__actions">
                <button type="submit" class="cart-quick-view__add button button--solid button--move" data-add>
                  <span class="button__text">Add to cart</span>
                  <span class="button__preloader" aria-hidden="true">
                    <svg viewBox="25 25 50 50" width="20" height="20"><circle cx="50" cy="50" r="20" fill="none" stroke="currentColor" stroke-width="4"/></svg>
                  </span>
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    `;
    document.body.appendChild(modal);
    return cacheRefs();
  }

  function cacheRefs() {
    panel = modal.querySelector('.cart-quick-view__panel');
    closeBtn = modal.querySelector('.cart-quick-view__close');
    statusEl = modal.querySelector('[data-status]');
    mediaEl = modal.querySelector('.cart-quick-view__media');
    imageEl = modal.querySelector('.cart-quick-view__image');
    titleEl = modal.querySelector('.cart-quick-view__title');
    priceEl = modal.querySelector('.cart-quick-view__price');
    descEl = modal.querySelector('.cart-quick-view__description');
    formEl = modal.querySelector('.cart-quick-view__form');
    variantPickerEl = modal.querySelector('.cart-quick-view__variants');
    qtyInputEl = modal.querySelector('.cart-quick-view__qty-input');
    addBtnEl = modal.querySelector('[data-add]');
    errorEl = modal.querySelector('[data-error]');
    return modal;
  }

  function extractHandle(href) {
    try {
      const url = new URL(href, window.location.origin);
      const m = url.pathname.match(/\/products\/([^/?#]+)/);
      return m ? decodeURIComponent(m[1]) : null;
    } catch {
      return null;
    }
  }

  function formatMoney(cents) {
    if (typeof window.Shopify !== 'undefined' && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents, window.Shopify.money_format);
    }
    return '$' + (cents / 100).toFixed(2);
  }

  // Compact money formatter: strips .00 for whole-dollar amounts so
  // $5 reads as "$5" not "$5.00" in variant dropdowns where the
  // amount IS the option (e.g., Pay It Forward donation tiers).
  function formatMoneyCompact(cents) {
    const dollars = cents / 100;
    if (dollars % 1 === 0) return '$' + dollars;
    return '$' + dollars.toFixed(2);
  }

  // Product handles that get a bespoke add-to-cart button label.
  // When we need this for more products, promote to a metafield or
  // settings map instead of growing this object forever.
  const CUSTOM_ADD_LABELS = {
    'pay-it-forward': 'Pay It Forward',
  };

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, c => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
    })[c]);
  }

  function setState(state) {
    modal.dataset.state = state;
  }

  async function fetchProduct(handle) {
    const cached = productCache.get(handle);
    if (cached && Date.now() - cached.ts < PRODUCT_CACHE_TTL_MS) {
      return cached.product;
    }
    const res = await fetch(`/products/${encodeURIComponent(handle)}.js`, {
      headers: { 'Accept': 'application/json' },
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const product = await res.json();
    productCache.set(handle, { product, ts: Date.now() });
    return product;
  }

  function applyInertBackground() {
    // Mark every sibling of <body>'s children except the modal as inert.
    // `inert` short-circuits focus AND removes the subtree from AT
    // traversal — stronger than aria-hidden which only helps screen
    // readers. Record which elements we mutated so we can restore them.
    inertedSiblings = [];
    Array.from(document.body.children).forEach(el => {
      if (el === modal) return;
      if (el.hasAttribute('inert')) return; // already inert, leave alone
      el.setAttribute('inert', '');
      inertedSiblings.push(el);
    });
  }

  function releaseInertBackground() {
    inertedSiblings.forEach(el => el.removeAttribute('inert'));
    inertedSiblings = [];
  }

  async function openFor(handle, href) {
    ensureModal();
    previouslyFocused = document.activeElement;
    modal.hidden = false;
    modal.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    applyInertBackground();
    setState('loading');
    statusEl.textContent = 'Loading\u2026';
    // Seed the title during loading so the dialog has an accessible name
    // from first render. aria-labelledby points at titleEl, and an empty
    // target resolves to an empty dialog name — screen readers would
    // announce "dialog" with no context during the 300-800ms fetch on
    // 3G webview. populate() overwrites this with the real product
    // title when the response lands.
    titleEl.textContent = 'Loading product\u2026';
    errorEl.hidden = true;
    closeBtn.focus();

    try {
      const product = await fetchProduct(handle);
      currentProduct = product;
      populate(product);
      setState('ready');
    } catch (err) {
      console.error('[cart-quick-view] fetch failed', err);
      statusEl.textContent = 'We couldn\u2019t load that product. Please try again.';
      setState('error');
    }
  }

  function populate(product) {
    titleEl.textContent = product.title || '';

    const img = product.featured_image || (product.images && product.images[0]) || '';
    if (img) {
      const resized = img.includes('?') ? img : `${img}?width=480`;
      imageEl.src = resized;
      imageEl.alt = product.title || '';
      mediaEl.style.display = '';
    } else {
      mediaEl.style.display = 'none';
    }

    renderVariantPicker(product);
    updatePriceDisplay(getSelectedVariant(product));

    const desc = (product.description || '').trim();
    descEl.innerHTML = truncateHtml(desc, 320);

    qtyInputEl.value = 1;

    // Per-product add-to-cart label override. Falls back to "Add to cart"
    // for every other product. Pay It Forward gets "Pay It Forward"
    // since the "Add to cart" framing feels off for a donation product.
    const label = CUSTOM_ADD_LABELS[product.handle] || 'Add to cart';
    const labelEl = addBtnEl.querySelector('.button__text');
    if (labelEl) labelEl.textContent = label;
  }

  function renderVariantPicker(product) {
    if (!variantPickerEl) return;
    // Single variant — hide picker entirely; /cart/add defaults to id.
    if (product.has_only_default_variant || (product.variants && product.variants.length <= 1)) {
      variantPickerEl.innerHTML = '';
      variantPickerEl.style.display = 'none';
      return;
    }
    variantPickerEl.style.display = '';
    // product.options comes from /products/{handle}.js as an array of
    // OBJECTS ({name, position, values}) in current Shopify — the
    // earlier code assumed strings and rendered "[object Object]" in
    // the label. Normalize either shape.
    const firstOpt = product.options && product.options[0];
    const optName = typeof firstOpt === 'string'
      ? firstOpt
      : (firstOpt && firstOpt.name) || 'Option';
    const label = product.options && product.options.length > 1 ? 'Options' : optName;
    // Dropdown label: if the variant title is purely numeric (e.g.
    // Pay It Forward donation tiers where option values ARE the dollar
    // amount), show just the formatted price. Otherwise the variant
    // title already carries the meaningful label ("Red / L"), so no
    // price concatenation — price is already displayed in the main
    // price row above, and updates when the selection changes.
    const options = product.variants.map(v => {
      const isNumericTitle = /^\d+(\.\d+)?$/.test(v.title);
      const labelText = isNumericTitle ? formatMoneyCompact(v.price) : v.title;
      const soldOut = !v.available ? ' (sold out)' : '';
      const disabled = !v.available ? ' disabled' : '';
      return `<option value="${v.id}"${disabled}>${escapeHtml(labelText)}${soldOut}</option>`;
    }).join('');
    variantPickerEl.innerHTML = `
      <label class="cart-quick-view__variant-label" for="${MODAL_ID}Variant">${escapeHtml(label)}</label>
      <select class="cart-quick-view__variant-select" id="${MODAL_ID}Variant" name="id" required>
        ${options}
      </select>
    `;
    const select = variantPickerEl.querySelector('select');
    const firstAvail = product.variants.find(v => v.available) || product.variants[0];
    if (firstAvail) select.value = firstAvail.id;
    select.addEventListener('change', () => {
      updatePriceDisplay(getSelectedVariant(product));
    });
  }

  function getSelectedVariant(product) {
    if (!product) return null;
    if (product.has_only_default_variant || product.variants.length === 1) {
      return product.variants[0];
    }
    const select = variantPickerEl.querySelector('select');
    if (!select) return product.variants[0];
    return product.variants.find(v => String(v.id) === select.value) || product.variants[0];
  }

  function updatePriceDisplay(variant) {
    if (!variant) {
      priceEl.textContent = '';
      return;
    }
    const price = formatMoney(variant.price);
    const hasCompare = variant.compare_at_price && variant.compare_at_price > variant.price;
    priceEl.innerHTML = hasCompare
      ? `${price} <s>${formatMoney(variant.compare_at_price)}</s>`
      : price;
  }

  function truncateHtml(html, maxChars) {
    if (!html) return '';
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    const text = tmp.textContent || '';
    if (text.length <= maxChars) return html;
    return `<p>${escapeHtml(text.slice(0, maxChars).trimEnd())}&hellip;</p>`;
  }

  async function addToCart(e) {
    e.preventDefault();
    if (!currentProduct) return;
    const variant = getSelectedVariant(currentProduct);
    if (!variant) return;
    if (!variant.available) {
      showError('This option is sold out. Please pick another.');
      return;
    }
    const qty = Math.max(1, parseInt(qtyInputEl.value, 10) || 1);
    errorEl.hidden = true;
    addBtnEl.classList.add('is-loading');
    addBtnEl.disabled = true;

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ id: variant.id, quantity: qty }),
      });
      if (!res.ok) {
        const errPayload = await res.json().catch(() => ({}));
        throw new Error(errPayload.description || errPayload.message || `HTTP ${res.status}`);
      }
      // Prefer the theme's existing cart-refresh path so we don't burn
      // a full page load on every add (frequent on PWYC tier comparison).
      // window.refreshCart() is defined in component-cart.js and handles
      // refetch + DOM diff + reward-threshold recompute. It's
      // fire-and-forget (no Promise return), so we just trigger it and
      // close the modal — the cart area will update a moment later.
      // Fall back to full reload when the helper isn't available.
      if (typeof window.refreshCart === 'function') {
        window.refreshCart();
        close();
        return;
      }
      window.location.reload();
    } catch (err) {
      console.error('[cart-quick-view] add-to-cart failed', err);
      showError(err.message || 'Something went wrong. Please try again.');
      addBtnEl.classList.remove('is-loading');
      addBtnEl.disabled = false;
    }
  }

  function showError(msg) {
    errorEl.textContent = msg;
    errorEl.hidden = false;
  }

  function close() {
    if (!modal || modal.hidden) return;
    modal.hidden = true;
    modal.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    releaseInertBackground();
    currentProduct = null;
    if (previouslyFocused && typeof previouslyFocused.focus === 'function') {
      previouslyFocused.focus();
    }
    previouslyFocused = null;
  }

  // Focus trap: when the modal is open, Tab at the last focusable
  // element wraps to the first, and Shift-Tab at the first wraps to
  // the last. Belt-and-braces with `inert` on siblings (which should
  // already prevent focus escape), but also ensures internal Tab order
  // stays within the modal even on older browsers that don't implement
  // inert.
  function getFocusable() {
    if (!panel) return [];
    return Array.from(panel.querySelectorAll(
      'a[href], button:not([disabled]), select:not([disabled]), input:not([disabled]):not([type="hidden"]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )).filter(el => !el.hidden && el.offsetParent !== null);
  }

  function trapFocus(e) {
    if (!modal || modal.hidden || e.key !== 'Tab') return;
    const focusable = getFocusable();
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  // ============ Event wiring ============

  // Trigger: product links inside opted-in scopes.
  document.addEventListener('click', (e) => {
    const anchor = e.target.closest('a[href]');
    if (!anchor) return;
    const scope = anchor.closest('[data-cart-quick-view-scope]');
    if (!scope) return;
    const handle = extractHandle(anchor.getAttribute('href'));
    if (!handle) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    if (e.button !== undefined && e.button !== 0) return;
    e.preventDefault();
    openFor(handle, anchor.href);
  });

  // Close via backdrop / close button / dismiss CTA
  document.addEventListener('click', (e) => {
    if (!modal || modal.hidden) return;
    if (e.target.closest('[data-close]')) {
      e.preventDefault();
      close();
    }
  });

  // Quantity stepper
  document.addEventListener('click', (e) => {
    if (!modal || modal.hidden) return;
    const btn = e.target.closest('[data-qty]');
    if (!btn) return;
    const delta = parseInt(btn.dataset.qty, 10) || 0;
    const cur = parseInt(qtyInputEl.value, 10) || 1;
    const next = Math.max(1, Math.min(99, cur + delta));
    qtyInputEl.value = next;
  });

  // Form submit (Add to cart)
  document.addEventListener('submit', (e) => {
    if (!modal || modal.hidden) return;
    if (e.target === formEl) addToCart(e);
  });

  // ESC to close + Tab focus trap
  document.addEventListener('keydown', (e) => {
    if (!modal || modal.hidden) return;
    if (e.key === 'Escape') close();
    else if (e.key === 'Tab') trapFocus(e);
  });
})();
